import { useEffect, useState } from "react";

import styles from "./SourceBadge.module.css";

/* Resolved once rather than on every render. Same PUBLIC_URL prefix as
   teamLogoSrc, so the badge survives being served from a sub-path. */
const VERIFIED_TICK_PATH = "/logos/verified.png";
const SOURCE_VARIANT_WIDTHS = Object.freeze([32, 48, 64]);

const LOGO_PIXELS = Object.freeze({
  sm: 15,
  md: 17,
  lg: 20,
});

const TICK_PIXELS = Object.freeze({
  sm: 12,
  md: 13,
  lg: 15,
});

const publicAsset = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${process.env.PUBLIC_URL ?? ""}${path.startsWith("/") ? path : `/${path}`}`;
};

const variantAsset = (path, width, format = "png") => {
  if (!path || /^https?:\/\//i.test(path) || !path.endsWith(".png")) {
    return publicAsset(path);
  }

  const slash = path.lastIndexOf("/");
  const directory = path.slice(0, slash);
  const name = path.slice(slash + 1, -4);
  return publicAsset(`${directory}/${width}/${name}.${format}`);
};

const variantSrcSet = (path, format = "png") =>
  SOURCE_VARIANT_WIDTHS.map(
    (width) => `${variantAsset(path, width, format)} ${width}w`
  ).join(", ");

/**
 * Where this game's data came from.
 *
 * Replaces the old "Real NFL schedule" text badge: a beginner recognises
 * the ESPN mark instantly, and the blue tick reads as "this is the real
 * thing" without needing to be read at all.
 *
 * The tick means **the data is real and comes from a named source**. It is
 * deliberately not an endorsement claim, and the title text says so in
 * words for anyone who hovers or uses a screen reader.
 *
 * Degrades like TeamLogo: no image, or an image that fails to load, falls
 * back to the source's name as text with the same tick and the same
 * meaning. Deleting public/logos/sources changes nothing but the picture.
 * The tick is its own image and degrades the same way: if it cannot load
 * we show nothing rather than a broken-image icon, and the title text
 * still says the source is verified.
 */
const SourceBadge = ({ source, size = "md", showName = true, className = "" }) => {
  const [failed, setFailed] = useState(false);
  const [tickFailed, setTickFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [source?.logo]);

  if (!source) return null;

  const sizeClass = styles[`size_${size}`] ?? styles.size_md;
  const showLogo = Boolean(source.logo) && !failed;
  const logoPixels = LOGO_PIXELS[size] ?? LOGO_PIXELS.md;
  const tickPixels = TICK_PIXELS[size] ?? TICK_PIXELS.md;

  return (
    <span
      className={`${styles.SourceBadge} ${sizeClass} ${
        source.verified ? styles.verified : styles.unverified
      } ${className}`.trim()}
      title={source.title}
    >
      {showLogo ? (
        <picture className={styles.logoPicture}>
          <source
            type="image/webp"
            srcSet={variantSrcSet(source.logo, "webp")}
            sizes={`${logoPixels}px`}
          />
          <source
            type="image/png"
            srcSet={variantSrcSet(source.logo, "png")}
            sizes={`${logoPixels}px`}
          />
          <img
            className={styles.logo}
            src={variantAsset(source.logo, 48, "png")}
            width={logoPixels}
            height={logoPixels}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
          />
        </picture>
      ) : (
        <span className={styles.wordmark}>{source.shortLabel}</span>
      )}

      {showName && showLogo && (
        <span className={styles.name}>{source.shortLabel}</span>
      )}

      {source.verified && !tickFailed && (
        <picture className={styles.tickPicture}>
          <source
            type="image/webp"
            srcSet={variantSrcSet(VERIFIED_TICK_PATH, "webp")}
            sizes={`${tickPixels}px`}
          />
          <source
            type="image/png"
            srcSet={variantSrcSet(VERIFIED_TICK_PATH, "png")}
            sizes={`${tickPixels}px`}
          />
          <img
            className={styles.tick}
            src={variantAsset(VERIFIED_TICK_PATH, 32, "png")}
            width={tickPixels}
            height={tickPixels}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            onError={() => setTickFailed(true)}
          />
        </picture>
      )}

      {/* The picture and the tick carry the meaning visually; this carries
          it for everyone else. */}
      <span className="visually-hidden">{source.title}</span>
    </span>
  );
};

export default SourceBadge;
